// src/components/BGP/BgpPage.tsx
import React, { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";

import { columns as baseColumns, BgpNeighbor } from "./BgpData";

import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";

// Filtro
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { makeBgpNeighborFields } from "./bgpNeighborFieldSpec";

import {
  getCTXRlistGlobal,
  getBgpNeighborsByCtxr,
  getLocalAddressesByCtxrId,
  updateBgpNeighbor,
  createBgpNeighbor,
  deleteBgpNeighbor,
} from "./request/request";

import { ReactComponent as IconEdit } from "../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../shared/assets/Icons/Icon_Delete.svg";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { SkeletonLine, SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";
import GenericCard from "@ui-components/Card/GenericCard";
import { ReactComponent as IconWarning } from "../../../shared/assets/Icons/Icon_Warning.svg";

interface CtxrItem {
  id: string;
  name: string;
  description: string;
  subscriptionId: number;
  Suscription?: string;
}

function BgpPage() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);

  // Overlay spinner
  const [isFetching, setIsFetching] = useState(false);

  // CTX-R list
  const [fullCTXRlist, setFullCTXRlist] = useState<CtxrItem[]>([]);
  const [instanceOptions, setInstanceOptions] = useState<string[]>([]);
  const [selectedInstance, setSelectedInstance] = useState("");

  // BGP neighbors
  const [neighbors, setNeighbors] = useState<BgpNeighbor[]>([]);

  // cni dropdown
  const [cniOptions, setCniOptions] = useState<string[]>([]);

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Form
  const [neighborId, setNeighborId] = useState<number | null>(null);
  const [neighborIP, setNeighborIP] = useState("");
  const [routerID, setRouterID] = useState("");
  const [localAS, setLocalAS] = useState("");
  const [peerAS, setPeerAS] = useState("");
  const [holdTime, setHoldTime] = useState("3");
  const [description, setDescription] = useState("");
  const [localAddress, setLocalAddress] = useState("Select a IP");
  const [vpn4, setVpn4] = useState(false);
  const [ipv4Unicast, setIpv4Unicast] = useState(false);
  const [ipv6Unicast, setIpv6Unicast] = useState(false);
  const [addPath, setAddPath] = useState<'disabled' | 'send' | 'receive' | 'send_receive'>('disabled');

  // Helper functions to map addPath values
  const addPathStringToNumber = (value: 'disabled' | 'send' | 'receive' | 'send_receive'): number => {
    const mapping: Record<string, number> = {
      'disabled': 0,
      'send': 1,
      'receive': 2,
      'send_receive': 3
    };
    return mapping[value] ?? 0;
  };

  const addPathNumberToString = (value: number | string | boolean | undefined): 'disabled' | 'send' | 'receive' | 'send_receive' => {
    if (typeof value === 'number') {
      const mapping: Record<number, 'disabled' | 'send' | 'receive' | 'send_receive'> = {
        0: 'disabled',
        1: 'send',
        2: 'receive',
        3: 'send_receive'
      };
      return mapping[value] ?? 'disabled';
    }
    if (typeof value === 'string') {
      return value as 'disabled' | 'send' | 'receive' | 'send_receive';
    }
    if (typeof value === 'boolean') {
      return value ? 'send_receive' : 'disabled';
    }
    return 'disabled';
  };

  // Para deshabilitar el botón mientras se realiza la petición de Guardar
  const [isSaving, setIsSaving] = useState(false);

  // Filtros con GenericSearchbar
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const bgpNeighborFields = useMemo(() => makeBgpNeighborFields(), []);

  // Generate suggested keys from field labels
  const suggestedKeys = useMemo(() => {
    return bgpNeighborFields.map((f) => f.label);
  }, [bgpNeighborFields]);

  // Build maps for filter normalization
  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<BgpNeighbor>>();
    for (const f of bgpNeighborFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [bgpNeighborFields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of bgpNeighborFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [bgpNeighborFields]);

  // Normalize filters
  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else {
          token.op = ":";
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  // Build predicate using advancedFilters system
  const predicate = useMemo(
    () => buildPredicate<BgpNeighbor>(normalizedFilters, bgpNeighborFields),
    [normalizedFilters, bgpNeighborFields]
  );

  // Apply predicate to filter data
  const filteredNeighbors = useMemo(
    () => neighbors.filter(predicate),
    [neighbors, predicate]
  );

  // DELETE flow
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<BgpNeighbor | null>(null);

  // Reutilizamos el RESULT ALERT para delete y ahora también para create/update
  const [isResultAlertOpen, setIsResultAlertOpen] = useState(false);
  const [alertVariant, setAlertVariant] = useState<"success" | "error">(
    "success"
  );
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");

  // 1) Cargar CTX-R
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;

    const fetchCTXRList = async () => {
      try {
        setIsFetching(true);
        const token = await getAccessTokenSilently();
        const response = await getCTXRlistGlobal(-1, tenantId, token);
        const dataArray: CtxrItem[] = response.data || [];

        setFullCTXRlist(dataArray);
        const mapped = dataArray.map(
          (item) => `${item.name} (${item.description})`
        );
        setInstanceOptions(mapped);

        if (mapped.length > 0) {
          const first = mapped[0];
          setSelectedInstance(first);

          const found = dataArray.find(
            (obj) => `${obj.name} (${obj.description})` === first
          );
          if (found) {
            await loadBgpNeighbors(found.id);
          }
        }
      } catch (err) {
        console.error("Error fetching CTXR list:", err);
      } finally {
        setIsFetching(false);
      }
    };

    fetchCTXRList();
  }, [isLoading, isAuthenticated, getAccessTokenSilently, tenantId]);

  // 2) Cambiar instancia => neighbors
  const handleInstanceSelect = async (selected: string) => {
    setSelectedInstance(selected);
    const foundObj = fullCTXRlist.find(
      (obj) => `${obj.name} (${obj.description})` === selected
    );
    if (!foundObj) return;
    await loadBgpNeighbors(foundObj.id);
  };

  // load neighbors
  const loadBgpNeighbors = async (ctxrId: string) => {
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      const resp = await getBgpNeighborsByCtxr(token, ctxrId, tenantId);
      setNeighbors(resp.data);
    } catch (error) {
      console.error("Error fetching BGP neighbors =>", error);
    } finally {
      setIsFetching(false);
    }
  };

  // Refresh
  const handleRefresh = async () => {
    const foundObj = fullCTXRlist.find(
      (obj) => `${obj.name} (${obj.description})` === selectedInstance
    );
    if (!foundObj) return;
    await loadBgpNeighbors(foundObj.id);
  };

  // Add BGP => form vacío
  const handleAddBGP = () => {
    setEditMode(false);
    setNeighborId(null);
    setNeighborIP("");
    setRouterID("");
    setLocalAS("");
    setPeerAS("");
    setHoldTime("3");
    setDescription("");
    setLocalAddress("Select a IP");
    setVpn4(false);
    setIpv4Unicast(false);
    setIpv6Unicast(false);
    setAddPath('disabled');
    // Abrir el modal inmediatamente, cargar direcciones locales después
    setIsModalOpen(true);

    // Cargar direcciones locales en background (no bloquea la apertura del modal)
    const foundObj = fullCTXRlist.find(
        (obj) => `${obj.name} (${obj.description})` === selectedInstance
    );
    if (foundObj) {
        loadLocalAddresses(foundObj.id).catch((err) => {
        console.error("Error loading local addresses for Add =>", err);
        });
    }

    // try {
    //   const foundObj = fullCTXRlist.find(
    //     (obj) => `${obj.name} (${obj.description})` === selectedInstance
    //   );
    //   if (foundObj) {
    //     await loadLocalAddresses(foundObj.id);
    //   }
    // } catch (err) {
    //   console.error("Error loading cni for Add =>", err);
    // }

    setIsModalOpen(true);
  };

  // Edit BGP => form con data
    // Edit BGP => form con data
    const handleEditBGP = (rowData: BgpNeighbor) => {
        setEditMode(true);
    
        setNeighborId(rowData.id);
        setNeighborIP(rowData.ipid);
        setRouterID(rowData.router_id);
        setLocalAS(String(rowData.local_as));
        setPeerAS(String(rowData.peer_as));
        setHoldTime(String(rowData.holdtime));
        setDescription(rowData.description ?? "");
        setLocalAddress(rowData.local_address);
    
        const families = (rowData.bgpfamily || "").toLowerCase();
        setVpn4(families.includes("mpls-vpn"));
        setIpv4Unicast(families.includes("ipv4") && families.includes("unicast"));
        setIpv6Unicast(families.includes("ipv6") && families.includes("unicast"));
        // Handle addPath: convert number/string/boolean to string value
        setAddPath(addPathNumberToString(rowData.addPath));
    
        // Abrir el modal inmediatamente, cargar direcciones locales después
        setIsModalOpen(true);
    
        // Cargar direcciones locales en background (no bloquea la apertura del modal)
        const foundObj = fullCTXRlist.find(
          (obj) => `${obj.name} (${obj.description})` === selectedInstance
        );
        if (foundObj) {
          loadLocalAddresses(foundObj.id).catch((error) => {
            console.error("Error loading local addresses for Edit =>", error);
          });
        }
      };
    
      // load cni (sin afectar isFetching para no bloquear la UI)
      const loadLocalAddresses = async (ctxrId: string) => {
        try {
          const token = await getAccessTokenSilently();
          const resp = await getLocalAddressesByCtxrId(token, ctxrId, tenantId);
          const items = resp.data.map(
            (item: { name: string; value: string }) => item.value
          );
          setCniOptions(items);
        } catch (err) {
          console.error("Error loading local addresses =>", err);
        }
      };
    
      // isFormValid
      const isFormValid = Boolean(
        neighborIP.trim() &&
          routerID.trim() &&
          localAddress !== "Select a IP" &&
          localAS.trim() &&
          peerAS.trim() &&
          holdTime.trim() &&
          (vpn4 || ipv4Unicast || ipv6Unicast)
      );
    
      // 6) Save BGP => success/error alert
      const handleSaveNeighbor = async () => {
        // Al hacer clic en Save, deshabilitamos el botón
        setIsSaving(true);
    
        try {
          const foundObj = fullCTXRlist.find(
            (obj) => `${obj.name} (${obj.description})` === selectedInstance
          );
          if (!foundObj) {
            console.warn("No CTXR found for:", selectedInstance);
            setIsModalOpen(false);
            return;
          }
          const ctxrId = foundObj.id;
    
          const token = await getAccessTokenSilently();
          const families: string[] = [];
          if (vpn4) families.push("ipv4 mpls-vpn");
          if (ipv4Unicast) families.push("ipv4 unicast");
          if (ipv6Unicast) families.push("ipv6 unicast");
    
          if (editMode && neighborId != null) {
            // Update
            await updateBgpNeighbor(token, ctxrId, neighborId, tenantId, {
              ipid: neighborIP,
              router_id: routerID,
              local_address: localAddress,
              local_as: Number(localAS) || 0,
              peer_as: Number(peerAS) || 0,
              bgpfamily: families,
              holdtime: holdTime,
              description: description,
              addPath: addPathStringToNumber(addPath),
            });
    
            // Tras actualizar => alert success
            setAlertVariant("success");
            setAlertTitle("Neighbor updated successfully!");
            setAlertDescription(`Neighbor "${neighborIP}" was updated.`);
          } else {
            // Create
            await createBgpNeighbor(token, ctxrId, tenantId, {
              ipid: neighborIP,
              router_id: routerID,
              local_address: localAddress,
              local_as: Number(localAS) || 0,
              peer_as: Number(peerAS) || 0,
              typeConfig: 0,
              bgpfamily: families,
              holdtime: Number(holdTime) || 3,
              bgppassword: "",
              description: description,
              addPath: addPathStringToNumber(addPath),
            });
    
            // Tras crear => alert success
            setAlertVariant("success");
            setAlertTitle("Neighbor created successfully!");
            setAlertDescription(`Neighbor "${neighborIP}" was created.`);
          }
    
          await loadBgpNeighbors(ctxrId);
        } catch (error) {
          console.error("Error saving BGP neighbor =>", error);
          // Manejamos error => alert
          setAlertVariant("error");
          setAlertTitle("Error saving neighbor");
          setAlertDescription("An error occurred while saving. Please try again.");
        } finally {
          // Rehabilitamos el botón y cerramos el modal
          setIsSaving(false);
          setIsModalOpen(false);
          // Mostramos la alerta final (success / error)
          setIsResultAlertOpen(true);
        }
      };

  // 7) Delete
  const handleDeleteClick = (rowData: BgpNeighbor) => {
    setRowToDelete(rowData);
    setIsAlertOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    setIsAlertOpen(false);

    try {
      setIsFetching(true);
      const foundObj = fullCTXRlist.find(
        (obj) => `${obj.name} (${obj.description})` === selectedInstance
      );
      if (!foundObj) {
        console.warn("No CTXR found for:", selectedInstance);
        return;
      }
      const ctxrId = foundObj.id;

      const token = await getAccessTokenSilently();
      await deleteBgpNeighbor(token, ctxrId, rowToDelete.id, tenantId);

      // recargar
      await loadBgpNeighbors(ctxrId);

      // => success
      setAlertVariant("success");
      setAlertTitle("Removed successfully!");
      setAlertDescription(`Neighbor "${rowToDelete.ipid}" was deleted.`);
    } catch (err) {
      console.error("Error deleting neighbor =>", err);
      setAlertVariant("error");
      setAlertTitle("Could not remove neighbor");
      setAlertDescription(
        `An error occurred removing "${rowToDelete.ipid}". Please try again.`
      );
    } finally {
      setIsFetching(false);
      setRowToDelete(null);
      setIsResultAlertOpen(true);
    }
  };

  const handleCancelDelete = () => {
    setIsAlertOpen(false);
    setRowToDelete(null);
  };

  // 8) Overwrite "Actions" col
  const finalColumns = baseColumns.map((col) => {
    if (col.header === "Actions" && col.renderCell) {
      return {
        ...col,
        renderCell: (row: BgpNeighbor) => (
          <div className="flex items-center gap-2">
            <button
              className="
                border border-semantic-border-secondary
                rounded
                bg-semantic-bg-primary
                text-semantic-fg-secondary
                hover:bg-semantic-state-active
                w-10 h-10 flex items-center justify-center
              "
              onClick={() => handleEditBGP(row)}
            >
              <IconEdit className="w-6 h-6" />
            </button>

            <button
              className="
                p-2
                border border-semantic-border-secondary
                rounded
                bg-semantic-bg-danger-secondary
                text-semantic-fg-danger
                hover:bg-red-100
                w-10 h-10 flex items-center justify-center
              "
              onClick={() => handleDeleteClick(row)}
            >
              <IconDelete className="w-5 h-5" />
            </button>
          </div>
        ),
      };
    }
    return col;
  });

  return (
    <div className="relative">
      {/* Header */}
      <div className="mb-4 p-4 bg-semantic-bg-primary border border-semantic-border-secondary rounded-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
          <div className="flex flex-col gap-3 w-full sm:w-auto sm:flex-row sm:items-end sm:gap-0 sm:space-x-4">
            {/* CTX-R instance + Refresh */}
            <div className="flex flex-col w-full sm:w-auto">
                {isFetching ? (
                <>
                    <SkeletonLine w={300} h={16} />
                </>
                ) : (
                <GenericDropdown
                    items={instanceOptions}
                    defaultLabel=""
                    value={selectedInstance}
                    onSelect={handleInstanceSelect}
                    minWidthClass="min-w-full sm:min-w-[300px]"
                    widthClass="w-full sm:w-60"
                    label="Select a CTX-R instance"
                    dropdownMaxHeightClass="max-h-48"
                />
                )}
            </div>

            <GenericButton
              label="Refresh"
              variant="secondary"
              shape="ghost"
              size="md"
              onClick={handleRefresh}
            />
          </div>

          {/* Add BGP neighbor */}
          <div className="flex items-center space-x-4 sm:justify-end">
            <GenericButton
              label="Add BGP neighbor"
              variant="brand"
              size="md"
              onClick={handleAddBGP}
            />
          </div>
        </div>
      </div>
      <GenericCard
        isCollapsible={false}
        isLoading={isLoading}
        bodyContent={
          <>
            {/* Row con el Filter */}
            <div className="mb-4 bg-[var(--semantic-bg-primary)]">
              <div className="w-full max-w-full sm:max-w-[500px] my-5 px-4 sm:px-0">
                <GenericSearchbar
                  placeholder="Find by attribute or value (e.g., Neighbor IP: 10.100.0.4)"
                  suggestedKeys={suggestedKeys}
                  onFiltersChange={setActiveFilters}
                  liveFiltering={true}
                  debounceMs={200}
                />
              </div>
            </div>
            {isFetching ? (
              <div className="p-4">
                <SkeletonTableRows
                  rows={5}
                  cols={5}
                  colWidths={["150px", "200px"]}
                  rowHeight={15}
                  rowGapClassName="space-y-3"
                />
              </div>
            ) : (
              <GenericTable
                columns={finalColumns}
                data={filteredNeighbors}
                initialRowsPerPage={10}
                rowsPerPageOptions={[5, 10]}
                width="100%"
                tableClassName="bg-semantic-bg-primary"
              />
            )}
          </>
        }
      />

      {/* Modal Add/Edit */}
      <GenericModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editMode ? "Edit BGP Neighbor" : "Add BGP Neighbor"}
        size="lg"
      >
        {/* CTX-R solo si estamos en modo Add */}
        {!editMode && (
          <div className="mb-4 flex flex-col items-center justify-center text-center">
            <p className="text-sm font-medium text-semantic-text-primary">
              Selected CTX-R Instance *
            </p>
            <p className="text-sm text-semantic-text-secondary mb-2">
              {selectedInstance || "No instance selected"}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="mb-1 text-sm font-medium text-semantic-text-primary">
              Neighbor IP <span className="text-red-500">*</span>
            </label>
            <GenericInput
              placeholder="e.g. 10.100.0.8"
              value={neighborIP}
              onChange={setNeighborIP}
            />
          </div>

          <div>
            <label className="mb-1 text-sm font-medium text-semantic-text-primary">
              Router ID <span className="text-red-500">*</span>
            </label>
            <GenericInput
              placeholder="e.g. 1.2.3.4"
              value={routerID}
              onChange={setRouterID}
            />
          </div>

          <div>
            <label className="mb-1 text-sm font-medium text-semantic-text-primary">
              Local Address <span className="text-red-500">*</span>
            </label>
            <GenericDropdown
              items={cniOptions.length > 0 ? cniOptions : ["Select a IP"]}
              value={localAddress}
              onSelect={setLocalAddress}
              widthClass="w-full"
              maxWidthClass="100%"
            />
          </div>

          <div>
            <label className="mb-1 text-sm font-medium text-semantic-text-primary">
              Local AS <span className="text-red-500">*</span>
            </label>
            <GenericInput
              placeholder="e.g. 65117"
              value={localAS}
              onChange={setLocalAS}
            />
          </div>

          <div>
            <label className="mb-1 text-sm font-medium text-semantic-text-primary">
              Peer AS <span className="text-red-500">*</span>
            </label>
            <GenericInput
              placeholder="e.g. 65118"
              value={peerAS}
              onChange={setPeerAS}
            />
          </div>

          <div>
            <label className="mb-1 text-sm font-medium text-semantic-text-primary">
              Hold Time <span className="text-red-500">*</span>
            </label>
            <GenericInput
              placeholder="3"
              value={holdTime}
              onChange={setHoldTime}
            />
          </div>

          <div className="col-span-3">
            <GenericInput
              label="Description"
              placeholder="(optional)"
              value={description}
              onChange={setDescription}
            />
          </div>
        </div>

        {/* BGP Families */}
        <div className="mb-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <span className="text-body font-medium text-semantic-text-secondary">BGP Families <span className="text-red-500">*</span></span>
              <GenericTooltip
                text="Select the BGP address families to enable for this neighbor session"
                position="top"
                icon={<IconWarning className="w-4 h-4 text-semantic-text-error" />}
              />
            </div>
            <div className="flex flex-col gap-2 items-start justify-start">
              <label className="flex items-center gap-2 cursor-pointer text-body">
                <input
                  type="checkbox"
                  checked={vpn4}
                  onChange={(e) => setVpn4(e.target.checked)}
                  className="accent-[var(--color-semantic-bg-brand-bold,#197278)] w-5 h-5 rounded border border-[var(--color-semantic-border-secondary,#DCDDE0)]"
                />
                <span className="text-semantic-text-primary">
                  VPNv4
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-body">
                <input
                  type="checkbox"
                  checked={ipv4Unicast}
                  onChange={(e) => setIpv4Unicast(e.target.checked)}
                  className="accent-[var(--color-semantic-bg-brand-bold,#197278)] w-5 h-5 rounded border border-[var(--color-semantic-border-secondary,#DCDDE0)]"
                />
                <span className="text-semantic-text-primary">
                  IPv4 Unicast
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-body">
                <input
                  type="checkbox"
                  checked={ipv6Unicast}
                  onChange={(e) => setIpv6Unicast(e.target.checked)}
                  className="accent-[var(--color-semantic-bg-brand-bold,#197278)] w-5 h-5 rounded border border-[var(--color-semantic-border-secondary,#DCDDE0)]"
                />
                <span className="text-semantic-text-primary">
                  IPv6 Unicast
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Capabilities - Add-Path */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium text-semantic-text-primary">Capabilities - Add-Path</span>
            <GenericTooltip
              text="Configure add-path capability for this BGP neighbor. Disabled: no add-path, Send: send multiple paths, Receive: receive multiple paths, Send/Receive: both directions"
              position="top"
              icon={<IconWarning className="w-4 h-4 text-semantic-text-error" />}
            />
          </div>
          <div className="max-w-full">
            <div className="flex flex-row w-full h-8 rounded-md border border-gray-300 bg-gray-100 overflow-hidden">
              {[
                { value: 'disabled', label: 'Disabled' },
                { value: 'send', label: 'Send' },
                { value: 'receive', label: 'Receive' },
                { value: 'send_receive', label: 'Send/Receive' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setAddPath(option.value as 'disabled' | 'send' | 'receive' | 'send_receive')}
                  className={`flex-1 px-2 py-1 flex items-center justify-center text-xs ${
                    addPath === option.value
                      ? "bg-white text-black font-semibold rounded-md border border-gray-300"
                      : "text-gray-500 hover:bg-gray-200"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <GenericButton
            label="Cancel"
            variant="secondary"
            size="md"
            onClick={() => setIsModalOpen(false)}
            disabled={isSaving}
          />
          <GenericButton
            label={isSaving ? "Saving..." : "Save"}
            variant="brand"
            size="md"
            onClick={handleSaveNeighbor}
            disabled={!isFormValid || isSaving}
            loading={isSaving}
          />
        </div>
      </GenericModal>

      {/* Delete confirm */}
      <GenericAlert
        isOpen={isAlertOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Are you sure you want to delete this item?"
        description={
          rowToDelete
            ? `Deleting "${rowToDelete.ipid}" cannot be undone.`
            : undefined
        }
        cancelLabel="Cancel"
        confirmLabel="Delete"
        variant="confirm"
      />

      {/* Result alert: success/error for both DELETE and CREATE/UPDATE */}
      <GenericAlert
        isOpen={isResultAlertOpen}
        onClose={() => setIsResultAlertOpen(false)}
        onConfirm={() => setIsResultAlertOpen(false)}
        title={alertTitle}
        description={alertDescription}
        cancelLabel="Close"
        confirmLabel="OK"
        variant={alertVariant}
      />
    </div>
  );
}

export default BgpPage;
